/**
 * SignInActivityPanel — Profile-page widget showing the caller's
 * recent security-relevant events + a "Sign out everywhere else" CTA.
 *
 * Lives at the bottom of /dashboard/profile. Pulls live from
 * GET /api/auth/activity (which reads from the `account_activity`
 * collection — distinct from compliance audit_logs so the Maker
 * can see their OWN activity even though they're cloaked elsewhere).
 *
 * Style: Palantir Gotham aesthetic — bracket headers, monospace,
 * thin dashed dividers, severity-coded icons.
 */
import React, { useEffect, useState } from 'react';
import {
  Shield, LogIn, AlertTriangle, KeyRound, Mail, RefreshCw,
  Slash, UserX, RotateCw, MapPin, Monitor, Clock,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

const ACTION_META = {
  auth_login_success:        { label: 'Signed in',                   icon: LogIn,         tone: 'emerald' },
  auth_login_failed:         { label: 'Failed sign-in attempt',      icon: Slash,         tone: 'amber'   },
  auth_login_rate_limited:   { label: 'Sign-in throttled',           icon: Shield,        tone: 'amber'   },
  auth_lockout_triggered:    { label: 'Account temporarily locked',  icon: AlertTriangle, tone: 'rose'    },
  magic_link_requested:      { label: 'Magic-link emailed',          icon: Mail,          tone: 'violet'  },
  magic_link_redeemed:       { label: 'Signed in via magic link',    icon: LogIn,         tone: 'emerald' },
  password_reset_requested:  { label: 'Password reset requested',    icon: KeyRound,      tone: 'amber'   },
  password_reset_via_link:   { label: 'Password reset',              icon: KeyRound,      tone: 'amber'   },
  password_changed:          { label: 'Password changed',            icon: KeyRound,      tone: 'amber'   },
  session_revoke_all:        { label: 'Signed out everywhere',       icon: RotateCw,      tone: 'violet'  },
  role_impersonation:        { label: 'Role impersonation',          icon: UserX,         tone: 'rose'    },
  user_role_changed:         { label: 'Role changed',                icon: UserX,         tone: 'violet'  },
};

const TONE_STYLES = {
  emerald: { bg: 'rgba(16,185,129,0.10)',  color: '#10B981', border: 'rgba(16,185,129,0.40)' },
  amber:   { bg: 'rgba(250,176,5,0.10)',   color: '#F59E0B', border: 'rgba(250,176,5,0.40)'  },
  rose:    { bg: 'rgba(244,63,94,0.10)',   color: '#F43F5E', border: 'rgba(244,63,94,0.40)'  },
  violet:  { bg: 'rgba(174,82,234,0.10)',  color: '#C084FC', border: 'rgba(174,82,234,0.40)' },
};

function deviceFromUA(ua) {
  if (!ua) return 'Unknown device';
  const s = ua;
  let browser = 'Browser';
  if (/Edg\//.test(s))           browser = 'Edge';
  else if (/Chrome\//.test(s))   browser = 'Chrome';
  else if (/Safari\//.test(s))   browser = 'Safari';
  else if (/Firefox\//.test(s))  browser = 'Firefox';
  let os = 'Device';
  if (/Mac OS X/.test(s))         os = 'macOS';
  else if (/Windows NT/.test(s))  os = 'Windows';
  else if (/Linux/.test(s) && /Android/.test(s) === false) os = 'Linux';
  else if (/Android/.test(s))     os = 'Android';
  else if (/iPhone|iPad/.test(s)) os = 'iOS';
  return `${browser} on ${os}`;
}

function relativeTime(iso) {
  if (!iso) return '';
  const then = new Date(iso);
  const diff = (Date.now() - then.getTime()) / 1000;
  if (diff < 60)        return 'just now';
  if (diff < 3600)      return `${Math.floor(diff / 60)} min ago`;
  if (diff < 86400)     return `${Math.floor(diff / 3600)} hr ago`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)} day${Math.floor(diff / 86400) === 1 ? '' : 's'} ago`;
  return then.toLocaleDateString();
}

export default function SignInActivityPanel({ isDark = true }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [revoking, setRevoking] = useState(false);

  /* eslint-disable -- v6 react-hooks/set-state-in-effect false-positive on the fetch effect. */
  const load = () => {
    setLoading(true);
    api.get('/auth/activity?limit=30')
      .then(({ data }) => setData(data))
      .catch((e) => toast.error(e.response?.data?.detail || 'Failed to load activity'))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);
  /* eslint-enable */

  const signOutEverywhere = async () => {
    if (!window.confirm(
      'Sign out of every OTHER device and browser? You will stay signed in HERE. '
      + 'All other sessions will need to log in again.',
    )) return;
    setRevoking(true);
    try {
      const { data: r } = await api.post('/auth/sign-out-everywhere');
      // Backend re-issued a fresh JWT + cookie to keep THIS tab signed in
      // past the new password_changed_at gate. Mirror into in-memory
      // bearer too in case any axios call fires before next page render.
      if (r?.token) {
        const { setToken } = await import('@/lib/tokenStore');
        setToken(r.token);
      }
      toast.success('All other sessions signed out.');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to revoke sessions');
    } finally { setRevoking(false); }
  };

  const events = data?.events || [];
  const pwdChangedAt = data?.password_changed_at;
  const acctCreatedAt = data?.account_created_at;

  return (
    <div
      className="pt-6 border-t border-slate-200 dark:border-white/10"
      data-testid="signin-activity-panel"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Shield size={14} className="text-[var(--ve-violet-bright)]" />
          <span className={`text-[10px] uppercase tracking-[0.22em] font-bold ${
            isDark ? 'text-[#D4AF37]' : 'text-[#7A5A00]'
          }`}>
            [ACCOUNT · SECURITY]
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={load}
            disabled={loading}
            data-testid="activity-refresh"
            className="px-2.5 py-1.5 text-[10px] uppercase tracking-[0.22em] font-bold rounded border border-slate-200 dark:border-white/10 hover:bg-slate-50 dark:hover:bg-white/5 disabled:opacity-50 flex items-center gap-1.5"
          >
            <RefreshCw size={11} className={loading ? 'animate-spin' : ''} />
            REFRESH
          </button>
          <button
            onClick={signOutEverywhere}
            disabled={revoking}
            data-testid="signout-everywhere-btn"
            className="px-3 py-1.5 text-[10px] uppercase tracking-[0.22em] font-bold rounded border bg-rose-500/10 text-rose-500 border-rose-500/40 hover:bg-rose-500/20 disabled:opacity-50 flex items-center gap-1.5"
          >
            <RotateCw size={11} />
            {revoking ? 'Revoking…' : 'Sign out everywhere else'}
          </button>
        </div>
      </div>

      <h2 className="display text-xl mt-2">Recent sign-in activity.</h2>
      <p className="text-xs text-slate-500 dark:text-white/55 mt-1">
        Every sign-in attempt, magic link, password reset and lockout on
        this account. Anything you don&apos;t recognize? Reset your
        password and sign out everywhere.
      </p>

      {/* Stat strip */}
      <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-2">
        <StatPill
          label="Last password change"
          value={pwdChangedAt ? relativeTime(pwdChangedAt) : 'Never'}
          testid="activity-pwd-changed"
        />
        <StatPill
          label="Account created"
          value={acctCreatedAt ? relativeTime(acctCreatedAt) : '—'}
          testid="activity-account-created"
        />
        <StatPill
          label="Events on file"
          value={loading ? '…' : (events.length || 0)}
          testid="activity-event-count"
        />
      </div>

      {/* Feed */}
      <div className="mt-5">
        {loading ? (
          <div className="text-xs text-slate-500 italic py-4 text-center">Loading activity…</div>
        ) : events.length === 0 ? (
          <div
            className="text-xs italic py-4 text-center text-slate-500 dark:text-white/55"
            data-testid="activity-empty"
          >
            No activity yet. As you sign in (or others try), events will appear here.
          </div>
        ) : (
          <ul
            className="space-y-1.5 max-h-[28rem] overflow-y-auto pr-1 ve-scrollbar"
            data-testid="activity-feed"
          >
            {events.map((e, i) => {
              const meta = ACTION_META[e.action] || {
                label: e.action.replace(/_/g, ' '),
                icon: Clock,
                tone: 'violet',
              };
              const tone = TONE_STYLES[meta.tone];
              const Icon = meta.icon;
              return (
                <li
                  key={i}
                  data-testid={`activity-event-${e.action}`}
                  className={`flex items-start gap-3 px-3 py-2.5 rounded-md border ${
                    isDark
                      ? 'border-white/5 bg-white/[0.02]'
                      : 'border-slate-200 bg-slate-50'
                  }`}
                >
                  <div
                    className="h-8 w-8 rounded flex items-center justify-center shrink-0 border"
                    style={{
                      backgroundColor: tone.bg,
                      color: tone.color,
                      borderColor: tone.border,
                    }}
                  >
                    <Icon size={14} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-baseline justify-between gap-2 flex-wrap">
                      <span className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                        {meta.label}
                      </span>
                      <span className="text-[10px] uppercase tracking-[0.18em] text-slate-500 dark:text-white/45 font-mono">
                        {relativeTime(e.at)}
                      </span>
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-slate-500 dark:text-white/55 font-mono">
                      {e.extra?.geo?.code && (
                        <span
                          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm border bg-[rgba(174,82,234,0.08)] text-[var(--ve-violet-bright)] border-[rgba(174,82,234,0.30)] text-[10px]"
                          title={e.extra.geo.name}
                        >
                          <span className="text-sm leading-none">{e.extra.geo.flag}</span>
                          <span className="font-semibold tracking-wide">{e.extra.geo.name}</span>
                        </span>
                      )}
                      {e.ip && (
                        <span className="inline-flex items-center gap-1">
                          <MapPin size={9} className="opacity-70" />
                          {e.ip}
                        </span>
                      )}
                      {e.ua && (
                        <span className="inline-flex items-center gap-1 truncate max-w-full">
                          <Monitor size={9} className="opacity-70" />
                          {deviceFromUA(e.ua)}
                        </span>
                      )}
                      {e.provider && e.provider !== 'password' && (
                        <span className="px-1.5 py-0.5 rounded-sm border bg-[rgba(174,82,234,0.08)] text-[var(--ve-violet-bright)] border-[rgba(174,82,234,0.30)] text-[9px] tracking-[0.16em] uppercase font-bold">
                          {e.provider}
                        </span>
                      )}
                      {e.extra?.attempts_in_window && (
                        <span className="text-rose-500">
                          {e.extra.attempts_in_window} attempts in window
                        </span>
                      )}
                      {e.extra?.is_new_user && (
                        <span className="px-1.5 py-0.5 rounded-sm border bg-emerald-500/10 text-emerald-500 border-emerald-500/40 text-[9px] tracking-[0.16em] uppercase font-bold">
                          NEW SIGNUP
                        </span>
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

function StatPill({ label, value, testid }) {
  return (
    <div
      className="rounded-md border border-slate-200 dark:border-white/10 bg-white/40 dark:bg-white/[0.03] px-3 py-2"
      data-testid={testid}
    >
      <div className="text-[9px] uppercase tracking-[0.22em] font-bold text-slate-500 dark:text-white/45">
        {label}
      </div>
      <div className="text-sm font-mono mt-1 truncate text-slate-900 dark:text-white">
        {value}
      </div>
    </div>
  );
}
